import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useDelivery } from 'api/hooks/useDelivery';
import { useDeviceReps } from 'api/hooks/useDeviceReps';
import { useNotifySurgeryCheckedOut } from 'api/hooks/useNotifySurgeryCheckedOut';
import { useOrder } from 'api/hooks/useOrder';
import { ShipperDetails } from 'api/models/Deliveries';
import { SetsByDelivery } from 'api/models/Sets';
import { useLanguage } from 'context/AppContext';
import { useCheckOutFlowParams } from 'hooks/useCheckOutFlowParams';
import { CheckOutTraysBySet, useCheckOutTraysBySet } from 'hooks/useCheckOutTraysBySet';
import useFilterCheckOutSetsByDelivery from 'hooks/useFilterCheckOutSetsByDelivery';
import { useSaveCheckOutTrayInspectionsForSet } from 'hooks/useSaveCheckOutTrayInspectionsForSet';
import { useVisibleCheckOutDialog } from 'hooks/useVisibleCheckOutDialog';
import { useGetRemainingSets } from './useGetRemainingSets';
import useGetShippablesOfType from './useGetShippablesOfType';
import { useIsTierZero } from 'hooks/useSubscriptionTier';
import { useReturnOrder } from 'api/hooks/useReturnOrder';
import { useReturnDelivery } from 'api/hooks/useReturnDelivery';
import { useParams } from 'react-router';

type CheckOutState = {
  setsByDelivery: SetsByDelivery;
  traysBySet: CheckOutTraysBySet;
};

export const useCheckOut = ({ deliveryId }: { deliveryId: string }) => {
  const [lang] = useLanguage();
  const [checkOutTraysBySet, setCheckOutTraysBySet] = useCheckOutTraysBySet();

  const { orderRecordId } = useParams<{ orderRecordId: string }>();

  const isTierZero = useIsTierZero();

  const { checkOutFlowParams, setCheckOutFlowParam } = useCheckOutFlowParams();
  const isSurgeryFlow = checkOutFlowParams.flowType == 'SURGERY';

  const { setVisibleCheckOutDialog } = useVisibleCheckOutDialog();

  const filterCheckOutSetsByDelivery = useFilterCheckOutSetsByDelivery();
  const getRemainingSets = useGetRemainingSets();

  const { data: delivery, refetch: refetchDelivery, isLoading: isDeliveryLoading } = useDelivery(deliveryId);
  const {
    data: order,
    refetch: fetchOrder,
    isLoading: isOrderLoading,
  } = useOrder(delivery?.orderRecordId || orderRecordId || '');

  const { mutateAsync: saveCheckOutTrayInspectionsForSet, isLoading: isSaveCheckOutTrayInspectionsForSetLoading } =
    useSaveCheckOutTrayInspectionsForSet({ deliveryId });
  const { mutateAsync: notifySurgeryCheckedOut, isLoading: isNotifySurgeryCheckedOutLoading } =
    useNotifySurgeryCheckedOut();
  const { mutateAsync: returnOrder, isLoading: isReturnOrderLoading } = useReturnOrder();
  const { mutateAsync: returnDelivery, isLoading: isReturnDeliveryLoading } = useReturnDelivery();

  const setHasRemainingTraysForCheckOut = useMemo(
    () =>
      (kitInstanceId: string, _checkOutTraysBySet: CheckOutTraysBySet = checkOutTraysBySet): boolean =>
        Object.values(_checkOutTraysBySet[kitInstanceId].trays).some(tray => !tray.completed),
    [checkOutTraysBySet],
  );

  const { haveShippables } = useGetShippablesOfType(delivery?.sets);

  // Shippable if any set is checked out, not yet notified, not a turnaround origin, and not
  // quick checked out with a non-shippable reason. This is per-set, so a mix of quick and
  // normal checkouts still triggers the shipment/return flow for the shippable sets.
  const hasShippableSets = haveShippables('sets');

  // warm the device reps cache for this supplier as soon as we know a shippable set exists, so
  // the "how is this equipment being returned?" dialog's supplier rep list is ready by the time
  // it mounts, instead of only starting the fetch when the dialog appears
  useDeviceReps(
    { supplier: delivery?.supplier?.tenantId, limit: '-1' },
    hasShippableSets && !!delivery?.supplier?.tenantId,
  );

  // this function has several obvious issues
  const navigateToNotifySupplierDialog = useCallback(
    (setKitInstanceId?: string): void => {
      // why is this undefined at this stage?
      if (!setVisibleCheckOutDialog) return;

      if (hasShippableSets) {
        setVisibleCheckOutDialog({
          type: 'SKIP_INSPECTION_SUCCESS',
          variant: checkOutFlowParams.setsByDelivery ? 'SETS' : 'DELIVERY',
        });
      }

      setVisibleCheckOutDialog(
        isSurgeryFlow
          ? {
              type: 'SURGERY_NOTIFY_SUPPLIER_CONFIRM',
            }
          : {
              type: 'NOTIFY_SUPPLIER_CONFIRM',
              setKitInstanceId: setKitInstanceId,
              exitFlow: true,
            },
      );
    },
    [checkOutFlowParams.setsByDelivery, hasShippableSets, isSurgeryFlow, setVisibleCheckOutDialog],
  );

  const saveSetCheckout = useCallback(
    async (kitInstanceId: string): Promise<CheckOutState> => {
      setVisibleCheckOutDialog({ type: 'SAVING_PLEASE_WAIT_DIALOG' });

      // the updated data is needed in the caller, before the state is updated
      const state: CheckOutState = {
        setsByDelivery: { ...checkOutFlowParams.setsByDelivery },
        traysBySet: { ...checkOutTraysBySet },
      };

      try {
        await saveCheckOutTrayInspectionsForSet(kitInstanceId);
      } catch (err) {
        console.error('saveSetCheckout error', err);
        toast.error(lang.CheckOutTrayDetailsPage.saveError);
        setVisibleCheckOutDialog(null);
        return state;
      }

      // update checkOutTraysBySet state
      delete state.traysBySet[kitInstanceId];
      setCheckOutTraysBySet(state.traysBySet);

      // update checkOutFlowParams state
      if (isSurgeryFlow) {
        const { data: order } = await fetchOrder();
        state.setsByDelivery = filterCheckOutSetsByDelivery(order?.sets || {});
        setCheckOutFlowParam('setsByDelivery', state.setsByDelivery);
      }

      return state;
    },
    [
      checkOutFlowParams.setsByDelivery,
      checkOutTraysBySet,
      fetchOrder,
      filterCheckOutSetsByDelivery,
      isSurgeryFlow,
      lang.CheckOutTrayDetailsPage.saveError,
      saveCheckOutTrayInspectionsForSet,
      setCheckOutFlowParam,
      setCheckOutTraysBySet,
      setVisibleCheckOutDialog,
    ],
  );

  const haveRemainingSetsForCheckOut = useCallback(
    async (
      _checkOutTraysBySet = checkOutTraysBySet,
      setsByDelivery = checkOutFlowParams.setsByDelivery,
      reloadDelivery = false,
    ): Promise<boolean> => {
      if (isSurgeryFlow) {
        return Object.keys(setsByDelivery).length > 0;
      }

      let sets = delivery?.sets;
      if (reloadDelivery) {
        const { data: delivery } = await refetchDelivery();
        sets = delivery?.sets;
      }
      if (!sets) return false;

      const remainingSets = getRemainingSets('CHECK_OUT', sets, _checkOutTraysBySet);
      return remainingSets.length > 0;
    },
    [
      checkOutFlowParams.setsByDelivery,
      checkOutTraysBySet,
      delivery?.sets,
      getRemainingSets,
      isSurgeryFlow,
      refetchDelivery,
    ],
  );

  const saveDeliveryReturn = useCallback(
    async (shipperDetails?: ShipperDetails, returnedByRepId?: string) => {
      // save the delivery return
      await (orderRecordId
        ? returnOrder({
            orderRecordId: delivery?.orderRecordId || orderRecordId || '',
            returnDetails: shipperDetails,
            returnedByRepId,
          })
        : returnDelivery({ deliveryId, returnDetails: shipperDetails, returnedByRepId }));

      // show the success dialog; the checkout document is generated on demand from there,
      // rather than automatically here
      setVisibleCheckOutDialog({
        type: orderRecordId ? 'SURGERY_CHECK_OUT_SUCCESS_DIALOG' : 'CHECK_OUT_SUCCESS_DIALOG',
        shipperDetails,
      });
    },
    [delivery?.orderRecordId, deliveryId, orderRecordId, returnDelivery, returnOrder, setVisibleCheckOutDialog],
  );

  // all sets are checked out, and may have shipper details in hand
  //  for notification. then show success dialog.
  // if not surgery flow then the check for implants to checkout is here
  //  else it was alsready done in onAfterSetSaved
  const saveDeliveryCheckOut = useCallback(
    async (shipperDetails?: ShipperDetails, returnedByRepId?: string, _checkOutTraysBySet = checkOutTraysBySet) => {
      // fall back to the rep captured during quick check out (SalesRep reason)
      const repId = returnedByRepId ?? checkOutFlowParams.returnedByRepId;

      // if isTierZero then this is a delivery return
      if (isTierZero) {
        await saveDeliveryReturn(shipperDetails, repId);
        return;
      }

      await Promise.all(Object.keys(_checkOutTraysBySet).map(saveSetCheckout));

      // only notify if something needs to be shipped back
      if (hasShippableSets) {
        notifySurgeryCheckedOut({
          surgeryId: order?.surgery.surgeryId || '',
          returnDetails: shipperDetails,
          returnedByRepId: repId,
        }).catch(() => toast.warn(lang.CheckOutTrayDetailsPage.notifyCheckedOutEmailFailed));
      }

      setCheckOutTraysBySet({}); // should be already so

      // if surgery flow show the success dialog
      if (isSurgeryFlow) {
        setVisibleCheckOutDialog({ type: 'SURGERY_CHECK_OUT_SUCCESS_DIALOG', shipperDetails });
        return;
      }

      setVisibleCheckOutDialog({ type: 'CHECK_OUT_SUCCESS_DIALOG', shipperDetails });
    },
    [
      checkOutFlowParams.returnedByRepId,
      checkOutTraysBySet,
      hasShippableSets,
      isSurgeryFlow,
      isTierZero,
      lang.CheckOutTrayDetailsPage.notifyCheckedOutEmailFailed,
      notifySurgeryCheckedOut,
      order?.surgery.surgeryId,
      saveDeliveryReturn,
      saveSetCheckout,
      setCheckOutTraysBySet,
      setVisibleCheckOutDialog,
    ],
  );

  // all sets are checked out, may need to collect shipper details
  //  and notify the supplier, then complete the checkout
  const collectShipmentDetailsOrSaveDelivery = useCallback(
    async ({
      shipperDetails,
      returnedByRepId,
      checkOutTraysBySet: _checkOutTraysBySet = checkOutTraysBySet,
      checkOutComplete = true,
      setKitInstanceId,
    }: {
      shipperDetails?: ShipperDetails;
      returnedByRepId?: string;
      checkOutTraysBySet?: CheckOutTraysBySet;
      checkOutComplete?: boolean;
      setKitInstanceId?: string;
    }) => {
      // if there are shippable sets in the flow, we need to notify the supplier
      // and then show the shipment details dialog
      // if there are not, we proceed without this
      const set = delivery?.sets.find(set => set.kitInstanceId === setKitInstanceId);

      // Collect shipment details / notify the supplier only when something is shippable:
      // either shippable sets remain, or a specific non-turnaround-origin set is being checked out.
      // When nothing is shippable (e.g. the whole delivery was quick checked out with a
      // non-shippable reason such as "already returned without Savi Capture"), skip straight to
      // saving and the success dialog.
      if (hasShippableSets || (setKitInstanceId && !set?.turnaroundOrigin)) {
        if (checkOutComplete) {
          setVisibleCheckOutDialog({
            type: 'SHIPMENT_DETAILS_DIALOG',
            setKitInstanceId,
          });
        } else {
          navigateToNotifySupplierDialog(setKitInstanceId);
        }
        return;
      }

      // nothing to ship — save and show the success dialog
      await saveDeliveryCheckOut(shipperDetails, returnedByRepId, _checkOutTraysBySet);
    },
    [
      checkOutTraysBySet,
      delivery?.sets,
      hasShippableSets,
      navigateToNotifySupplierDialog,
      saveDeliveryCheckOut,
      setVisibleCheckOutDialog,
    ],
  );

  const onAfterSetSaved = useCallback(
    async ({ stateUpdate }: { stateUpdate?: CheckOutState; setKitInstanceId: string }) => {
      // after each set checkout, go to the "what next" checkpoint
      // the checkpoint dialog handles sets, implants, and "I'm done" options
      const haveSetsForCheckOut = await haveRemainingSetsForCheckOut(
        stateUpdate?.traysBySet,
        stateUpdate?.setsByDelivery,
        !!stateUpdate,
      );

      console.groupCollapsed('onAfterSetSaved');
      setVisibleCheckOutDialog({
        type: 'CHECK_OUT_WHAT_NEXT',
        haveSetsForCheckOut,
      });
    },
    [haveRemainingSetsForCheckOut, setVisibleCheckOutDialog],
  );

  const completeCheckOutForSet = useCallback(
    async (setKitInstanceId: string) => {
      const haveTraysForCheckOut = setHasRemainingTraysForCheckOut(setKitInstanceId);
      if (haveTraysForCheckOut) throw new Error('Unexpected state: All trays should be processed');

      // save the now inspected set
      const update = await saveSetCheckout(setKitInstanceId);
      await onAfterSetSaved({ stateUpdate: update, setKitInstanceId });
    },
    [onAfterSetSaved, saveSetCheckout, setHasRemainingTraysForCheckOut],
  );

  return {
    saveSetCheckout,
    saveDeliveryCheckOut,
    collectShipmentDetailsOrSaveDelivery,
    completeCheckOutForSet,
    navigateToNotifySupplierDialog,
    isLoading:
      isSaveCheckOutTrayInspectionsForSetLoading ||
      isNotifySurgeryCheckedOutLoading ||
      isReturnOrderLoading ||
      isReturnDeliveryLoading ||
      isDeliveryLoading ||
      isOrderLoading,
  };
};
